import type { CharacterVisit, VisitWindowUnit } from '../domain/types.js';

const UNIT_MS: Record<VisitWindowUnit, number> = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
};

function windowMs(window: { n: number; unit: VisitWindowUnit }): number {
  return window.n * UNIT_MS[window.unit];
}

/** Roll arrival and departure once, preserving any values already persisted. */
export function rollVisit(visit: CharacterVisit, random = Math.random): CharacterVisit {
  const startMs = new Date(visit.startAt).getTime();
  const endMs = new Date(visit.endAt).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return visit;
  const arrival = visit.rolledArrivalAt
    ?? new Date(startMs + random() * windowMs(visit.startWindow)).toISOString();
  const departureBase = endMs;
  const departure = visit.rolledDepartureAt
    ?? new Date(departureBase + random() * windowMs(visit.endWindow)).toISOString();
  const arrivalMs = new Date(arrival).getTime();
  const departureMs = new Date(departure).getTime();
  const clampedDeparture = visit.rolledDepartureAt
    ?? (departureMs < arrivalMs ? arrival : departure);
  return {
    ...visit,
    rolledArrivalAt: arrival,
    rolledDepartureAt: clampedDeparture,
  };
}

export function rollVisits(visits: CharacterVisit[]): CharacterVisit[] {
  return visits.map((visit) => rollVisit(visit));
}

export function isVisitActive(nowIso: string, visit: CharacterVisit): boolean {
  if (!visit.rolledArrivalAt || !visit.rolledDepartureAt) return false;
  const now = new Date(nowIso).getTime();
  const arrival = new Date(visit.rolledArrivalAt).getTime();
  const departure = new Date(visit.rolledDepartureAt).getTime();
  return now >= arrival && now <= departure;
}

export function isCharacterPresent(
  nowIso: string,
  visitor: boolean | undefined,
  visits?: CharacterVisit[],
): boolean {
  if (visitor !== true) return true;
  if (!visits || visits.length === 0) return false;
  return visits.some((visit) => isVisitActive(nowIso, visit));
}
